import { PaginatedApiResponse } from "@/types/api.types";
import baseApi from "./RootBaseApi";
import { ModerationState } from "@/types/service-management.types";

export interface ModerationListItem {
  id: string;
  name: string;
  description?: string;
  moderation_state: ModerationState;
  moderation_reason?: string | null;
  moderated_at?: string | null;
  submitted_at?: string | null;
  created_at: string;
  updated_at: string;
  owner_type: "HARBOUR" | "SERVICE_PROVIDER" | string;
  owner_name: string;
  harbor_name?: string;
  harbor_manager_email?: string;
}

interface ModerationListQueryParams {
  moderation_state: ModerationState;
  page?: number;
  limit?: number;
  search?: string;
}

interface ModerateServicePayload {
  moderation_state: Exclude<
    ModerationState,
    ModerationState.DRAFT | ModerationState.SUBMITTED
  >;
  moderation_reason?: string;
}

export const ServiceModerationService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getModerationServices: builder.query<
      PaginatedApiResponse<ModerationListItem[]>,
      ModerationListQueryParams
    >({
      query: ({ moderation_state, ...rest }) => ({
        url: `/service-providers/services/moderation/${moderation_state}`,
        method: "GET",
        params: rest,
      }),
      transformResponse: (response: {
        status?: boolean;
        message?: string;
        data: ModerationListItem[];
        totalItems?: number;
        total?: number;
        currentPage?: number;
        page?: number;
        limit?: number;
        totalPages?: number;
      }) => {
        const totalRaw = response.totalItems ?? response.total;
        const pageRaw = response.currentPage ?? response.page;
        const limitRaw = response.limit;
        const totalItems = Number(totalRaw ?? response.data?.length ?? 0);
        const pageLimit = limitRaw !== undefined ? Number(limitRaw) : undefined;
        const currentPage = Number(pageRaw ?? 1);
        const totalPages =
          response.totalPages ??
          (pageLimit ? Math.ceil(totalItems / pageLimit) : undefined);

        return {
          data: response.data as ModerationListItem[],
          totalItems,
          totalPages,
          currentPage,
          status: response.status,
          message: response.message,
        } as PaginatedApiResponse<ModerationListItem[]>;
      },
      providesTags: ["Services"],
      keepUnusedDataFor: 0,
    }),

    moderateService: builder.mutation<
      { status: boolean; message: string },
      { id: string; payload: ModerateServicePayload }
    >({
      query: ({ id, payload }) => ({
        url: `/service-providers/services/${id}/moderate`,
        method: "PATCH",
        data: payload,
        headers: { "Content-Type": "application/json" },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Services", id },
        "Services",
      ],
    }),

    getModerationCounts: builder.query<
      { status: boolean; message?: string; counts: Record<string, number> },
      void
    >({
      query: () => ({
        url: "/service-providers/services/moderation/counts",
        method: "GET",
      }),
      transformResponse: (response: {
        status?: boolean;
        message?: string;
        data?: Partial<Record<string, unknown>>;
        counts?: Partial<Record<string, unknown>>;
      }) => {
        // Normalize to { counts: { SUBMITTED, APPROVED, REJECTED, DISABLED } }
        const data = (response?.data ?? response?.counts ?? {}) as Record<
          string,
          unknown
        >;
        const toNum = (v: unknown) => (v == null || v === "" ? 0 : Number(v));
        const counts = {
          SUBMITTED: toNum(data.SUBMITTED ?? data.submitted),
          APPROVED: toNum(data.APPROVED ?? data.approved),
          REJECTED: toNum(data.REJECTED ?? data.rejected),
          DISABLED: toNum(data.DISABLED ?? data.disabled),
        } as Record<string, number>;
        return {
          status: !!response?.status,
          message: response?.message,
          counts,
        };
      },
      providesTags: ["Services"],
    }),
  }),
});

export const {
  useGetModerationServicesQuery,
  useModerateServiceMutation,
  useGetModerationCountsQuery,
} = ServiceModerationService;
